/**
 * UserListPage - Admin page for managing users
 *
 * Route: /admin/users
 *
 * URL State:
 * - status: "all" | "active" | "disabled" (default: all)
 * - role: "all" | "investor" | "agent" | "admin" (default: all)
 * - search: string (default: empty)
 *
 * Role-based behavior:
 * - Admin only: View all users with edit/disable/enable actions
 * - Admins cannot modify other admin users
 */

import { useState, useCallback, useMemo } from 'react'
import { useNavigate, Link, useSearch } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { getErrorMessage } from '@/common/utils/errors'
import { DataTable } from '@/common/components/data-table'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { sprinkles } from '@/core/styles/sprinkles.css'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { AlertDialog } from '@/common/components/alert-dialog'
import { DropdownMenu } from '@/common/components/dropdown-menu'
import { Badge } from '@/common/components/badge'
import { ExportUsersModal } from '@/features/admin-users/components/ExportUsersModal'
import { useAdminUsersSource } from '@/features/admin-users/data/adminUsersSource'
import {
  disableUser,
  enableUser,
  resetPassword,
  setPassword,
  type AdminUserItem,
} from '@/features/admin-users/api/adminUsers'
import { AdminUsersToolbar } from '@/features/admin-users/components/AdminUsersToolbar'
import { SetPasswordDialog } from '@/features/admin-users/components/SetPasswordDialog'
import type { AdminUserFilters } from '@/features/admin-users/data/adminUsersSource'
import * as styles from './index.css'

export const Route = createFileRoute({
  component: UserListPage,
})

// Map role to Badge color
const ROLE_BADGE_COLOR: Record<string, 'caution' | 'info' | 'success' | 'neutral'> = {
  admin: 'caution',
  agent: 'info',
  investor: 'success',
}

// =============================================================================
// URL State Helpers
// =============================================================================

type StatusValue = 'all' | 'active' | 'disabled'
type RoleValue = 'all' | 'investor' | 'agent' | 'admin'
type SourceValue = 'all' | 'invited' | 'requested'
type OnboardedValue = 'all' | 'yes' | 'no'
type AgentTypeValue = 'all' | 'internal' | 'external'

function parseUrlStatus(value: string | null): StatusValue {
  if (value === 'active' || value === 'disabled') return value
  return 'all'
}

function parseUrlRole(value: string | null): RoleValue {
  if (value === 'investor' || value === 'agent' || value === 'admin') return value
  return 'all'
}

function parseUrlSource(value: string | null): SourceValue {
  if (value === 'invited' || value === 'requested') return value
  return 'all'
}

function parseUrlOnboarded(value: string | null): OnboardedValue {
  if (value === 'yes' || value === 'no') return value
  return 'all'
}

function parseUrlAgentType(value: string | null): AgentTypeValue {
  if (value === 'internal' || value === 'external') return value
  return 'all'
}

function onboardedToBoolean(onboarded: OnboardedValue): boolean | undefined {
  if (onboarded === 'yes') return true
  if (onboarded === 'no') return false
  return undefined
}

function statusToActive(status: StatusValue): boolean | undefined {
  if (status === 'active') return true
  if (status === 'disabled') return false
  return undefined
}

// =============================================================================
// Display Helpers
// =============================================================================

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function getDisplayName(user: AdminUserItem): string {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`
  }
  if (user.first_name) return user.first_name
  if (user.last_name) return user.last_name
  return '-'
}

function getPrimaryRole(roles: string[]): string {
  if (roles.includes('admin')) return 'admin'
  if (roles.includes('agent')) return 'agent'
  if (roles.includes('investor')) return 'investor'
  return roles[0] || 'unknown'
}

// =============================================================================
// Main Component
// =============================================================================

function UserListPage() {
  const { roles, token } = useAuth()
  const { showSuccessToast, showErrorToast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const source = useAdminUsersSource()
  const search = useSearch({ strict: false }) as { status?: string; role?: string; source?: string; onboarded?: string; agent_type?: string; search?: string }

  // Parse URL state
  const urlStatus = parseUrlStatus(search.status ?? null)
  const urlRole = parseUrlRole(search.role ?? null)
  const urlSource = parseUrlSource(search.source ?? null)
  const urlOnboarded = parseUrlOnboarded(search.onboarded ?? null)
  // Agent-type filter is only valid when role=agent; ignore stale URL values
  // when the user switches to a different role to avoid silently filtering an
  // investor list down to zero rows.
  const urlAgentType = urlRole === 'agent' ? parseUrlAgentType(search.agent_type ?? null) : 'all'
  const urlSearch = search.search ?? ''

  // Convert URL state to filters
  const filters = useMemo<AdminUserFilters>(
    () => ({
      active: statusToActive(urlStatus),
      role: urlRole === 'all' ? undefined : urlRole,
      signup_source: urlSource === 'all' ? undefined : urlSource,
      onboarded: onboardedToBoolean(urlOnboarded),
      agent_type: urlAgentType === 'all' ? undefined : urlAgentType,
    }),
    [urlStatus, urlRole, urlSource, urlOnboarded, urlAgentType]
  )

  // Update URL params helper
  const updateUrl = useCallback(
    (updates: { status?: StatusValue; role?: RoleValue; source?: SourceValue; onboarded?: OnboardedValue; agent_type?: AgentTypeValue; search?: string }) => {
      navigate({
        search: ((prev: Record<string, string | undefined>) => {
          const next = { ...prev }
          // Status: only set if not default ("all")
          if (updates.status !== undefined) {
            if (updates.status === 'all') {
              delete next.status
            } else {
              next.status = updates.status
            }
          }
          // Role: only set if not default ("all")
          if (updates.role !== undefined) {
            if (updates.role === 'all') {
              delete next.role
            } else {
              next.role = updates.role
            }
          }
          // Source: only set if not default ("all")
          if (updates.source !== undefined) {
            if (updates.source === 'all') {
              delete next.source
            } else {
              next.source = updates.source
            }
          }
          // Onboarded: only set if not default ("all")
          if (updates.onboarded !== undefined) {
            if (updates.onboarded === 'all') {
              delete next.onboarded
            } else {
              next.onboarded = updates.onboarded
            }
          }
          // Agent type: only set if not default ("all"). Force-clear whenever
          // the resulting role is not agent — this guards against a stale
          // agent_type leaking through when handleFiltersChange spreads the
          // old filter object while the user is switching roles.
          const effectiveRole =
            updates.role !== undefined ? updates.role : (next.role as RoleValue | undefined) ?? 'all'
          if (effectiveRole !== 'agent') {
            delete next.agent_type
          } else if (updates.agent_type !== undefined) {
            if (updates.agent_type === 'all') {
              delete next.agent_type
            } else {
              next.agent_type = updates.agent_type
            }
          }
          // Search: only set if not empty
          if (updates.search !== undefined) {
            if (updates.search === '') {
              delete next.search
            } else {
              next.search = updates.search
            }
          }
          return next
        }) as never,
        replace: true,
      })
    },
    [navigate]
  )

  // Handle filter change from toolbar
  const handleFiltersChange = useCallback(
    (newFilters: AdminUserFilters) => {
      const newStatus: StatusValue =
        newFilters.active === undefined ? 'all' : newFilters.active ? 'active' : 'disabled'
      const newRole: RoleValue = (newFilters.role as RoleValue) || 'all'
      const newSource: SourceValue = (newFilters.signup_source as SourceValue) || 'all'
      const newOnboarded: OnboardedValue =
        newFilters.onboarded === undefined ? 'all' : newFilters.onboarded ? 'yes' : 'no'
      const newAgentType: AgentTypeValue = (newFilters.agent_type as AgentTypeValue) || 'all'
      updateUrl({ status: newStatus, role: newRole, source: newSource, onboarded: newOnboarded, agent_type: newAgentType })
    },
    [updateUrl]
  )

  // Handle search change from toolbar
  const handleSearchChange = useCallback(
    (value: string) => {
      updateUrl({ search: value })
    },
    [updateUrl]
  )

  // Toggle status confirmation state
  const [toggleTarget, setToggleTarget] = useState<AdminUserItem | null>(null)
  const [isToggling, setIsToggling] = useState(false)

  // Reset email confirmation state
  const [resetEmailTarget, setResetEmailTarget] = useState<AdminUserItem | null>(null)
  const [isSendingResetEmail, setIsSendingResetEmail] = useState(false)

  // Password dialog state
  const [setPasswordTarget, setSetPasswordTarget] = useState<AdminUserItem | null>(null)

  // Export modal state
  const [exportModalOpen, setExportModalOpen] = useState(false)

  // Check if current user is admin
  const isAdmin = roles.includes('admin')

  // Handle edit user
  const handleEdit = useCallback(
    (user: AdminUserItem) => {
      navigate({ to: '/admin/users/$id', params: { id: user.id } })
    },
    [navigate]
  )

  // Handle toggle status - open confirmation
  const handleToggleClick = useCallback((user: AdminUserItem) => {
    setToggleTarget(user)
  }, [])

  // Handle confirmed toggle
  const handleConfirmToggle = useCallback(async () => {
    if (!token || !toggleTarget) return

    setIsToggling(true)
    try {
      if (toggleTarget.active) {
        await disableUser(token, toggleTarget.id)
        showSuccessToast(`User "${toggleTarget.email}" has been disabled.`)
      } else {
        await enableUser(token, toggleTarget.id)
        showSuccessToast(`User "${toggleTarget.email}" has been enabled.`)
      }
      setToggleTarget(null)
      // Invalidate the admin-users query cache to refresh the list
      await queryClient.invalidateQueries({ queryKey: ['dataSource', 'admin-users'] })
    } catch (err) {
      showErrorToast(getErrorMessage(err) || 'Failed to update user status')
    } finally {
      setIsToggling(false)
    }
  }, [token, toggleTarget, showSuccessToast, showErrorToast, queryClient])

  // Handle send password reset email - open confirmation
  const handleResetPasswordClick = useCallback((user: AdminUserItem) => {
    setResetEmailTarget(user)
  }, [])

  // Handle confirmed reset email send
  const handleConfirmResetEmail = useCallback(async () => {
    if (!token || !resetEmailTarget) return

    setIsSendingResetEmail(true)
    try {
      await resetPassword(token, resetEmailTarget.id)
      showSuccessToast(`Password reset email sent to ${resetEmailTarget.email}`)
      setResetEmailTarget(null)
    } catch (err) {
      showErrorToast(getErrorMessage(err) || 'Failed to send password reset email')
    } finally {
      setIsSendingResetEmail(false)
    }
  }, [token, resetEmailTarget, showSuccessToast, showErrorToast])

  // Handle set password dialog submit
  const handleSetPassword = useCallback(
    async (password: string, requireChangeOnLogin: boolean) => {
      if (!token || !setPasswordTarget) return

      await setPassword(token, setPasswordTarget.id, {
        password,
        require_change_on_login: requireChangeOnLogin,
      })
      showSuccessToast(`Password set for ${setPasswordTarget.email}`)
    },
    [token, setPasswordTarget, showSuccessToast]
  )

  // Non-admin unauthorized view
  if (!isAdmin) {
    return (
      <Stack gap="24" style={{ padding: '24px', height: '100%' }}>
        <Stack gap="4">
          <Text as="h1" variant="h4" weight="bold">
            User Management
          </Text>
          <Text variant="bodySm" color="muted">
            Manage platform users
          </Text>
        </Stack>
        <Stack gap="16" align="center" justify="center" style={{ flex: 1 }}>
          <Icon name="lock" size={48} style={{ color: '#999' }} />
          <Text as="h2" variant="bodyLg" weight="bold">
            Access Restricted
          </Text>
          <Text variant="body" color="muted" style={{ textAlign: 'center', maxWidth: 400 }}>
            User management is only available to administrators.
          </Text>
          <Link to="/properties">
            <Button>Go to Properties</Button>
          </Link>
        </Stack>
      </Stack>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Page Header */}
        <div className={styles.header}>
          <Stack gap="4">
            <Text as="h1" variant="h4" weight="bold">
              User Management
            </Text>
            <Text
              variant="bodySm"
              color="muted"
              className={sprinkles({ display: { base: 'block', tabletDown: 'none' } })}
            >
              View and manage platform users
            </Text>
          </Stack>
          <div className={styles.headerButtons}>
            <Button variant="secondary" size="sm" onClick={() => setExportModalOpen(true)}>
              <Icon name="download" size={18} />
              Export
            </Button>
            <Link to="/admin/users/bulk-update">
              <Button variant="secondary" size="sm">
                <Icon name="upload" size={18} />
                Bulk Update
              </Button>
            </Link>
            <Link to="/admin/users/new">
              <Button size="sm">
                <Icon name="person_add" size={18} />
                Create
              </Button>
            </Link>
          </div>
        </div>

        {/* DataTable */}
        <div className={styles.tableContainer}>
        <DataTable
          source={source}
          getRowKey={(u) => u.id}
          aria-label="Users list"
          getRowLabel={(u) => `${u.first_name} ${u.last_name}, ${u.email}`}
          initialFilters={filters as Record<string, unknown>}
          initialSearch={urlSearch}
          emptyMessage="No users found"
          hoverable
          stickyHeader
          pinnedLast
          additionalFields={['roles', 'verified', 'created_at', 'onboarding_completed_at', 'signup_source', 'agent_type']}
        >
          <DataTable.Toolbar>
            <AdminUsersToolbar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              search={urlSearch}
              onSearchChange={handleSearchChange}
            />
          </DataTable.Toolbar>

          <DataTable.Columns>
            {/* Role + Agent Type */}
            <DataTable.Column id="role" header="Role" field={false} width={120}>
              {(user: AdminUserItem) => {
                const role = getPrimaryRole(user.roles)
                return (
                  <Stack gap="4">
                    <Badge color={ROLE_BADGE_COLOR[role] ?? 'neutral'} style={{ textTransform: 'capitalize' }}>
                      {role}
                    </Badge>
                    {role === 'agent' && user.agent_type && (
                      <Text variant="bodyXs" color="muted" style={{ textTransform: 'capitalize' }}>
                        {user.agent_type}
                      </Text>
                    )}
                  </Stack>
                )
              }}
            </DataTable.Column>

            {/* Email & Name */}
            <DataTable.Column id="email" header="User" field="email" width={280}>
              {(user: AdminUserItem) => (
                <Stack gap="4">
                  <Text weight="medium" variant="bodySm">
                    {user.email}
                  </Text>
                  <Text variant="bodyXs" color="muted">
                    {getDisplayName(user)}
                  </Text>
                </Stack>
              )}
            </DataTable.Column>

            {/* Company */}
            <DataTable.Column id="company" header="Company" field="company_name" width={180}>
              {(user: AdminUserItem) => (
                <Text variant="bodySm" color={user.company_name ? 'default' : 'muted'}>
                  {user.company_name || '-'}
                </Text>
              )}
            </DataTable.Column>

            {/* Signup Source */}
            <DataTable.Column id="signup_source" header="Source" field="signup_source" width={100}>
              {(user: AdminUserItem) => (
                <Text variant="bodySm" color="muted" style={{ textTransform: 'capitalize' }}>
                  {user.signup_source || '-'}
                </Text>
              )}
            </DataTable.Column>

            {/* Onboarded */}
            <DataTable.Column id="onboarded" header="Onboarded" field="onboarding_completed_at" width={120}>
              {(user: AdminUserItem) => (
                <Text variant="bodySm" color="muted">
                  {formatDateTime(user.onboarding_completed_at)}
                </Text>
              )}
            </DataTable.Column>

            {/* Status */}
            <DataTable.Column id="status" header="Status" field="active" width={90}>
              {(user: AdminUserItem) => (
                <Badge color={user.active ? 'success' : 'error'}>
                  {user.active ? 'Active' : 'Disabled'}
                </Badge>
              )}
            </DataTable.Column>

            {/* Last Login */}
            <DataTable.Column id="last_login" header="Last Login" field="last_login_at" width={120}>
              {(user: AdminUserItem) => (
                <Text variant="bodySm" color="muted">
                  {formatDateTime(user.last_login_at)}
                </Text>
              )}
            </DataTable.Column>

            {/* Actions */}
            <DataTable.Column id="actions" header="" field={false} width={40} align="right">
              {(user: AdminUserItem) => {
                const isAdminUser = user.roles.includes('admin')
                if (isAdminUser) {
                  return null
                }
                return (
                  <DropdownMenu>
                    <DropdownMenu.Trigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Icon name="more_vert" size={18} />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                      <DropdownMenu.Item onSelect={() => handleEdit(user)}>
                        <Icon name="edit" size={16} />
                        Edit
                      </DropdownMenu.Item>
                      <DropdownMenu.Item
                        onSelect={() => handleToggleClick(user)}
                      >
                        <Icon name={user.active ? 'block' : 'check_circle'} size={16} />
                        {user.active ? 'Disable' : 'Enable'}
                      </DropdownMenu.Item>
                      {user.active && (
                        <>
                          <DropdownMenu.Separator />
                          <DropdownMenu.Item onSelect={() => handleResetPasswordClick(user)}>
                            <Icon name="email" size={16} />
                            Send Reset Email
                          </DropdownMenu.Item>
                          <DropdownMenu.Item onSelect={() => setSetPasswordTarget(user)}>
                            <Icon name="key" size={16} />
                            Set Password
                          </DropdownMenu.Item>
                        </>
                      )}
                    </DropdownMenu.Content>
                  </DropdownMenu>
                )
              }}
            </DataTable.Column>
          </DataTable.Columns>
        </DataTable>
      </div>

      {/* Toggle status confirmation dialog */}
      <AlertDialog
        open={!!toggleTarget}
        onOpenChange={(open) => !open && setToggleTarget(null)}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>
            {toggleTarget?.active ? 'Disable' : 'Enable'} user?
          </AlertDialog.Title>
          <AlertDialog.Description>
            {toggleTarget?.active
              ? `Are you sure you want to disable "${toggleTarget?.email}"? They will not be able to log in until re-enabled.`
              : `Are you sure you want to enable "${toggleTarget?.email}"? They will be able to log in again.`}
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={isToggling}>Cancel</AlertDialog.Cancel>
            <Button
              variant={toggleTarget?.active ? 'danger' : 'primary'}
              onClick={handleConfirmToggle}
              disabled={isToggling}
            >
              {isToggling
                ? toggleTarget?.active
                  ? 'Disabling...'
                  : 'Enabling...'
                : toggleTarget?.active
                  ? 'Disable'
                  : 'Enable'}
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>

      {/* Reset email confirmation dialog */}
      <AlertDialog
        open={!!resetEmailTarget}
        onOpenChange={(open) => !open && setResetEmailTarget(null)}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>Send password reset email?</AlertDialog.Title>
          <AlertDialog.Description>
            A password reset email will be sent to "{resetEmailTarget?.email}".
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={isSendingResetEmail}>Cancel</AlertDialog.Cancel>
            <Button
              variant="primary"
              onClick={handleConfirmResetEmail}
              disabled={isSendingResetEmail}
            >
              {isSendingResetEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>

      {/* Set password dialog */}
      <SetPasswordDialog
        open={!!setPasswordTarget}
        onOpenChange={(open) => !open && setSetPasswordTarget(null)}
        userEmail={setPasswordTarget?.email || ''}
        onSubmit={handleSetPassword}
      />

      {/* Export users modal */}
      <ExportUsersModal open={exportModalOpen} onOpenChange={setExportModalOpen} />
      </div>
    </div>
  )
}
